import mapValues from 'lodash/mapValues';

import type { UtilityContext } from '@change-corgi/core/react/utilityContext';
import type { PsfShareCopyBanditType } from '@change-corgi/middle-end/gql';

import { NotFoundError } from 'src/shared/error';

import type { PsfShareDataQuery, PsfShareDataQueryVariables } from './api.graphql';
import { PsfShareData } from './api.graphql';
import type { ShareFeedItem } from './shareFeed';
import { getShareFeed } from './shareFeed';

type Petition = NonNullable<PsfShareDataQuery['petition']>;
type Bandits = Petition['bandits'];
export type PsfSharePageData = {
	petition: Omit<Petition, 'bandits'>;
	bandits: {
		[key in keyof Omit<Bandits, 'id'>]: Omit<Bandits[key], 'id' | 'variants'> & {
			overridden: boolean;
		};
	};
	shareFeed: readonly ShareFeedItem[];
	/**
	 * skip is when coming from the promote_or_share page
	 *
	 * message is when having shared once from the share page
	 */
	type: PsfShareCopyBanditType;
	/**
	 * false when having shared once from the share page
	 */
	initial: boolean;
	/**
	 * true when coming from the promote_or_share page
	 */
	skip: boolean;
	/**
	 * temporary estimation of signs from shares with hard-coded percentage
	 */
	signaturesFromShareCountEstimate: number;
	/**
	 * temporary estimation of signs from shares with hard-coded percentage
	 */
	signaturesFromShareCountEstimateLocalized: string;
};
export type PetitionHeadlineBandit = Petition['headlineBandits']['copylinkHeadline'];
export type PetitionMitaBandit = Petition['mitaBandits']['mitaSmartImage'];

/**
 * FIXME this hard-coded value might have been set as a temporary value for estimating sign count from shares
 * until that metric would be properly computed in our backend, which seems to never have been properly planned
 * or prioritized
 */
const SIGNS_FROM_SHARE_ESTIMATION_PERCENTAGE = 0.6;

export async function getPsfSharePageData(
	utilityContext: UtilityContext,
	slug: string,
	{
		queryParams,
		banditOverrides,
	}: {
		queryParams: { skip?: string; share?: string };
		banditOverrides: { copy?: string };
	},
): Promise<PsfSharePageData> {
	const {
		gql: { fetch },
	} = utilityContext;

	const skip = !!queryParams.skip;
	const initial = !queryParams.share;
	const type = (() => {
		if (skip) return 'SKIP';
		return initial ? 'INITIAL' : 'MESSAGE';
	})();

	const [{ data }, shareFeed] = await Promise.all([
		fetch<PsfShareDataQuery, PsfShareDataQueryVariables>({
			query: PsfShareData,
			variables: {
				slugOrId: slug,
				type,
				includeBanditCopyOverride: !!banditOverrides.copy,
				banditCopyOverrideVariantName: banditOverrides.copy || '',
			},
			rejectOnError: true,
		}),
		getShareFeed(utilityContext, slug),
	]);

	if (!data?.petition) {
		throw new NotFoundError();
	}

	return normalizeData(utilityContext, { skip, initial, type, petition: data.petition, shareFeed });
}

function normalizeData(
	{ i18n }: UtilityContext,
	{
		skip,
		initial,
		type,
		petition,
		shareFeed,
	}: {
		skip: boolean;
		initial: boolean;
		type: PsfShareCopyBanditType;
		petition: Petition;
		shareFeed: readonly ShareFeedItem[];
	},
): PsfSharePageData {
	const signaturesFromShareCountEstimate = Math.round(
		petition.signatureState.signatureCount.displayed * SIGNS_FROM_SHARE_ESTIMATION_PERCENTAGE,
	);

	const {
		bandits: { id, ...petitionBandits },
		...partialPetition
	} = petition;

	const bandits = mapValues(petitionBandits, ({ variants, ...variant }) => ({
		...variant,
		...(variants?.overrideVariant ? { variant: variants.overrideVariant, overridden: true } : { overridden: false }),
	}));

	return {
		petition: partialPetition,
		bandits,
		shareFeed,
		skip,
		initial,
		type,
		signaturesFromShareCountEstimate,
		signaturesFromShareCountEstimateLocalized: i18n.localizeNumber(signaturesFromShareCountEstimate),
	};
}
